import {Attributes, AttributesRow, Card, DataStatus, Flags} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {useGetBranchQuery, useReadCodelistQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetRentalVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {CommentsWidget} from '@dms/shared';

import {composePath, parseDate} from 'shared/web';

interface VehicleRentalProps {
  vehicleId: string;
}

export function VehicleRental(props: VehicleRentalProps) {
  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId: props.vehicleId});
  const {data: branchData} = useGetBranchQuery(
    {branchId: rentalVehicle?.branchId ?? ''},
    {
      skip: !rentalVehicle?.branchId,
    }
  );

  const formatDateTimeShort = useDateTimeFormatter();
  const formatNumber = useFormatNumber();

  const {
    data: carParkData,
    isLoading: isLoadingCarPark,
    isError: isCarParkError,
  } = useReadCodelistQuery({codelistId: 'carPark'});

  const {
    data: equipmentData,
    isLoading: isLoadingEquipment,
    isError: isEquipmentError,
  } = useReadCodelistQuery({codelistId: 'equipment'});

  const isLoading = isLoadingCarPark || isLoadingEquipment;
  const isError = isCarParkError || isEquipmentError;

  const carParkName = carParkData?.codes?.find(
    (code) => code.codeId === rentalVehicle?.carParkId
  )?.name;
  const equipmentNames = equipmentData?.codes
    ?.filter((code) => rentalVehicle?.equipment?.includes(code.codeId))
    .map((code) => ({
      label: code.name,
    }));

  const rows: AttributesRow[] = rentalVehicle
    ? [
        {
          label: i18n.t('entity.rental.labels.carParkId'),
          value: carParkName,
        },
        {
          label: i18n.t('general.labels.active'),
          value: rentalVehicle.active ? i18n.t('general.labels.yes') : i18n.t('general.labels.no'),
        },
        {
          label: i18n.t('general.labels.status'),
          value: i18n.t(`entity.rental.labels.${rentalVehicle.state}`),
        },
        {
          label: i18n.t('entity.rental.labels.availableFrom'),
          value: rentalVehicle.availability.from
            ? formatDateTimeShort('dateTimeShort', parseDate(rentalVehicle.availability.from))
            : null,
        },
        {
          label: i18n.t('entity.rental.labels.availableTo'),
          value: rentalVehicle.availability.to
            ? formatDateTimeShort('dateTimeShort', parseDate(rentalVehicle.availability.to))
            : null,
        },
        {
          label: i18n.t('entity.rental.labels.mileageLimit'),
          value:
            rentalVehicle.availability.mileageLimit !== null
              ? `${formatNumber(rentalVehicle.availability.mileageLimit)} ${i18n.t('general.metric.km')}`
              : null,
        },
        {
          label: i18n.t('entity.rental.labels.note'),
          value: rentalVehicle.note,
        },
        {
          label: i18n.t('entity.rental.labels.equipment'),
          content: (
            <Flags flags={equipmentNames} size="small" isSubtle colorScheme="blue" shouldWrap />
          ),
        },
      ]
    : [];

  return (
    <Grid columns={2}>
      <Card
        title={i18n.t('entity.vehicle.labels.rentalInformation')}
        actions={[
          {
            type: 'button',
            variant: 'link',
            to: composePath(vehiclesRoutes.editRentalVehicleV2, {params: {id: props.vehicleId}}),
            leftIcon: 'image/edit',
            title: i18n.t('general.actions.edit'),
            size: 'small',
          },
        ]}
      >
        <DataStatus isLoading={isLoading} isError={isError}>
          <Attributes rows={rows} />
        </DataStatus>
      </Card>

      <VStack>
        <CommentsWidget
          resourceId={EntityResourceIds.vehicle}
          recordId={props.vehicleId}
          title={i18n.t('entity.vehicle.labels.vehicleComments')}
        />
      </VStack>
    </Grid>
  );
}
